/**
 * `Built<T>` — nominal brand applied to a contract body AFTER it has passed
 * the wire schema's `safeParse`. The transport seam
 * (`lib/api/post-json.ts::postJson`) accepts ONLY `Built<T>` bodies, so a
 * hand-rolled literal in a component cannot reach `fetch('/api/...')`
 * unless it goes through a canonical builder that parses + seals.
 *
 * Mechanism:
 *  - `seal<T>(parsed: T): Built<T>` is the only legitimate constructor —
 *    structurally a no-op cast at runtime; safety is from type discipline.
 *  - The brand is a unique symbol, so `Built<T>` cannot be obtained by
 *    structural means (`as Built<T>` is detectable; `seal` import is
 *    gated by ESLint).
 *  - ESLint guard (see `eslint.config.mjs`): `seal` import and `as Built<…>`
 *    cast are forbidden outside `lib/**` and `packages/contract/**`.
 *
 * Why this is a SEPARATE step from the contract Zod schemas:
 *  - The schemas (`ChatSendRequestSchema`, etc.) only verify the body is
 *    well-shaped. They do NOT prevent a developer from skipping safeParse
 *    and posting a hand-rolled object directly. The brand closes that gap.
 *  - Sibling-E fork-prevention rationale (forked builder dropped
 *    `currency`, 2026-06-11): a structural `satisfies` pin catches a
 *    field-loss IF the forked literal is type-checked against the
 *    contract type. The brand goes one step further — the seam refuses
 *    to send anything that didn't pass through a parse.
 *
 * Scope (Sibling-E §4 #3c, first increment):
 *  - This file + `lib/api/post-json.ts` are the canonical seam.
 *  - First adopters: `lib/chat/chat-upsert.ts` + `lib/chat/chat-send.ts`.
 *  - NOT yet retrofitted: `lib/payments/checkout-actions.ts`,
 *    `lib/offers/offer-builders.ts`, `lib/auctions/auction-builders.ts`,
 *    `lib/orders/order-mutations.ts`, `lib/cart-quick-checkout.ts`. Each
 *    increment lands with its own smoke verification — see
 *    `.tondocs/RELIABILITY_ROADMAP.md §4 #3c`.
 *
 * @see lib/api/post-json.ts — transport seam accepting only `Built<T>`
 * @see lib/chat/chat-upsert.ts — first adopter (B1)
 * @see lib/chat/chat-send.ts — first adopter (B2)
 * @see eslint.config.mjs — `seal` import + `as Built<…>` cast guard
 * @see .tondocs/RELIABILITY_ROADMAP.md §4 #3c
 */

declare const builtBrand: unique symbol;

/**
 * Nominal brand on the body of a wire request. The brand is added by
 * `seal()` after the body has been validated by its Zod schema. The
 * transport seam accepts only `Built<T>`.
 */
export type Built<T> = T & { readonly [builtBrand]: true };

/**
 * Mark `parsed` as a sealed wire body. Call ONLY after the value passes
 * the appropriate Zod `safeParse`. Calling on an un-parsed value is a
 * programmer-bug: it satisfies the type checker but defeats the brand.
 *
 * This function is structurally a no-op cast at runtime. The teeth are:
 *  - the `Built<T>` type cannot be constructed by any other means
 *    (unique-symbol brand); and
 *  - the ESLint guard prevents `seal` import + `as Built<…>` cast outside
 *    `lib/**` and `packages/contract/**`.
 */
export function seal<T>(parsed: T): Built<T> {
  return parsed as Built<T>;
}
